/**
 * 找服务商品数据（企业服务 + 单品资源，统一卡片模型）。
 * - offers 结构分两类：flat（一维数组，单选驱动）与 matrix（二维，两级联动选择）。
 * - rows 描述配置区行的顺序与呈现：static 只读展示 offer[bind]；select 为可切换下拉。
 *   dim 指明该下拉驱动哪一维：flat 用 "i"；matrix 用 "row"（第一维）/"col"（第二维）。
 * - featured 为主推商品（Token Plan / Dumate），承载更多介绍与能力亮点。
 * - category 决定商品归属的一级 Tab：enterprise（企业服务）/ token（Token算力）/ ai（通用AI应用）。
 * - offer.url 存在时，「立即购买」直接跳转该地址；否则回落到活动页。
 */

export type ProductCategory = "enterprise" | "token" | "ai";

export type Offer = Record<string, string> & { campaignId: string; url?: string };

export type ProductRow =
  | { select: false; label: string; bind: string; wrap?: boolean }
  | { select: true; label: string; bind: string; dim: "i" | "row" | "col" };

export type Product = {
  key: string;
  category: ProductCategory;
  title: string;
  desc: string;
  featured?: boolean;
  highlights?: string[];
  rows: ProductRow[];
  /** flat: Offer[]；matrix: Offer[][] */
  offers: Offer[] | Offer[][];
};

/** 一级 Tab 定义（顺序即展示顺序）。 */
export const CATEGORIES: { key: ProductCategory; label: string }[] = [
  { key: "enterprise", label: "企业服务" },
  { key: "token", label: "Token算力" },
  { key: "ai", label: "通用AI应用" },
];

export const PRODUCTS: Product[] = [
  {
    key: "biz_tax",
    category: "enterprise",
    title: "工商财税",
    desc: "精选最受欢迎的工商财税服务，从公司注册到个体户办理，解决您的创业难题。",
    rows: [
      { select: true, label: "服务类型", bind: "variant", dim: "i" },
      { select: false, label: "服务亮点", bind: "feature", wrap: true },
    ],
    offers: [
      { variant: "云上园区注册", feature: "1元送刻章，送地址，可远程办理", price: "¥1起", originPrice: "", discount: "热门", limit: "", campaignId: "", url: "https://cloud.baidu.com/product/cbs#/park" },
      { variant: "本地公司注册", feature: "工商专家为您省心办理，无隐形收费", price: "¥9.9起", originPrice: "", discount: "推荐", limit: "", campaignId: "", url: "https://cloud.baidu.com/product/crs#/" },
      { variant: "个体工商户注册", feature: "含注册挂靠地址、代办营业执照服务", price: "¥500/次", originPrice: "", discount: "新品", limit: "", campaignId: "", url: "https://console.bce.baidu.com/cbs/#/park/order/submit?stepStart=2&comType=INDIVIDUAL&area=CP0003&taxpayerType=SMALL_SCALE_TAXPAYER&packageType=7" },
    ],
  },
  {
    key: "trademark",
    category: "enterprise",
    title: "商标知产",
    desc: "智能申请注册、专家辅助注册、至尊无忧注册等多种模式，满足不同用户需求。",
    rows: [
      { select: true, label: "注册模式", bind: "variant", dim: "i" },
      { select: false, label: "服务亮点", bind: "feature", wrap: true },
    ],
    offers: [
      { variant: "智能申请注册", feature: "0服务费，自助办理，人工严审", price: "¥275起", originPrice: "¥328", discount: "新客特惠", limit: "限购1件", campaignId: "", url: "https://console.bce.baidu.com/tms/#/tms/create/self?pageResource=PORTAL_001" },
      { variant: "专家辅助注册", feature: "专家一对一全流程代办，规避风险提升成功率", price: "¥539", originPrice: "", discount: "", limit: "", campaignId: "", url: "https://console.bce.baidu.com/tms/#/tms/create/advance?type=AUXILIARY_REGISTRATION&pageResource=PORTAL_001" },
      { variant: "指定类目至尊无忧注册", feature: "你注册我投保，指定类目驳回赔付全款", price: "¥599", originPrice: "", discount: "", limit: "", campaignId: "", url: "https://console.bce.baidu.com/tms/#/tms/create/advance?type=GUARANTEE_REGISTRATION_ONE_CLS&pageResource=PORTAL_001" },
    ],
  },
  {
    key: "domain",
    category: "enterprise",
    title: "域名服务",
    desc: "百余款域名后缀随心选，新客注册 COM 域名仅需 33 元，一站式查询、注册与管理。",
    rows: [
      { select: true, label: "域名后缀", bind: "variant", dim: "i" },
      { select: false, label: "适用场景", bind: "feature", wrap: true },
    ],
    offers: [
      { variant: ".com 域名", feature: "全球通用，商业首选，新客专享价", price: "¥33起", originPrice: "", discount: "新客专享", limit: "", campaignId: "", url: "https://cloud.baidu.com/product/bcd.html" },
      { variant: ".cn 域名", feature: "国内企业官网优选，中文品牌标识", price: "¥23起", originPrice: "", discount: "", limit: "", campaignId: "", url: "https://cloud.baidu.com/product/bcd.html" },
    ],
  },
  {
    key: "ssl_cert",
    category: "enterprise",
    title: "SSL证书",
    desc: "精选知名品牌 SSL 证书，SHA2 & ECC 加密，保障网站信息传输安全与公信力。",
    rows: [
      { select: true, label: "证书类型", bind: "variant", dim: "i" },
      { select: false, label: "适合场景", bind: "feature", wrap: true },
    ],
    offers: [
      { variant: "DV型SSL证书", feature: "适合小微企业/API服务/个人网站", price: "¥288起/年", originPrice: "", discount: "", limit: "", campaignId: "", url: "https://console.bce.baidu.com/cas/#/cas/apply/create~brand=SSLTRUS&certType=DV&productType=SINGLE&company=baidu" },
      { variant: "OV型SSL证书", feature: "适合企业应用、官网、电商，验证企业身份", price: "¥988起/年", originPrice: "", discount: "", limit: "", campaignId: "", url: "https://console.bce.baidu.com/cas/#/cas/apply/create~brand=SSLTRUS&certType=OV&productType=SINGLE&company=baidu" },
      { variant: "EV型SSL证书", feature: "适合金融平台、大中企业、政府机关，绿色地址栏", price: "¥2588起/年", originPrice: "", discount: "", limit: "", campaignId: "", url: "https://console.bce.baidu.com/cas/#/cas/apply/create~brand=SSLTRUS&certType=EV&productType=SINGLE&company=baidu" },
    ],
  },
  {
    key: "sms",
    category: "enterprise",
    title: "智能消息服务",
    desc: "支持验证码下发、通知类消息、营销类消息等多种场景，三网覆盖，稳定可靠触达。",
    rows: [
      { select: true, label: "套餐类型", bind: "variant", dim: "i" },
      { select: false, label: "套餐说明", bind: "feature", wrap: true },
    ],
    offers: [
      { variant: "国内短信小套餐包", feature: "100条 · 按量用包 · 24个月", price: "¥0.99", originPrice: "¥4.5", discount: "限时2.2折起", limit: "限购1个", campaignId: "", url: "https://cloud.baidu.com/campaign/order.html#/sms/activity~product=activitysms&config=20250101_discount_first_buy_sms_100_24m&qps=1" },
      { variant: "国内短信大套餐包", feature: "10万条 · 按量用包 · 24个月", price: "¥3900", originPrice: "", discount: "", limit: "", campaignId: "", url: "https://console.bce.baidu.com/smsv3/?_=1606962684640#/smsv3/gnpackage/create?size=fiveThousand" },
      { variant: "国际/港澳台流量包", feature: "指定地区文本短信 · 按量用包 · 12个月", price: "¥450", originPrice: "", discount: "", limit: "", campaignId: "", url: "https://console.bce.baidu.com/smsv3/?" },
    ],
  },
  {
    key: "qianfan_token_plan",
    category: "token",
    title: "百度千帆Token Plan 企业版",
    desc: "提供优质模型，以坐席订阅、灵活分配、积分共享作为管理方案，助力企业灵活控制 AI 成本。",
    featured: true,
    highlights: ["一份订阅畅用多种大模型", "按需配置团队席位、共享积分", "覆盖开发 / 创作 / 研究 / 办公"],
    rows: [
      { select: false, label: "版本", bind: "version" },
      { select: true, label: "套餐", bind: "packageName", dim: "i" },
      { select: false, label: "座席数量", bind: "seats" },
      { select: false, label: "时长", bind: "duration" },
    ],
    offers: [
      { version: "企业版", packageName: "标准版", seats: "1-99可选", duration: "1个月", price: "¥419", originPrice: "¥598", discount: "7折", limit: "", campaignId: "20251014_discount_buy_wenxinfactory_token_plan_standard_1m" },
      { version: "企业版", packageName: "高级版", seats: "1-99可选", duration: "1个月", price: "¥989", originPrice: "¥1498", discount: "6.6折", limit: "", campaignId: "20251014_discount_buy_wenxinfactory_token_plan_advanced_1m" },
    ],
  },
  {
    key: "dumate_enterprise",
    category: "ai",
    title: "DuMate 企业版",
    desc: "面向企业级任务处理的通用智能体，提供组织协同、知识沉淀与资源管理等能力。",
    highlights: ["企业级通用智能体", "组织协同与知识沉淀", "统一资源管理"],
    rows: [
      { select: false, label: "付费方式", bind: "payType" },
      { select: true, label: "规格", bind: "spec", dim: "i" },
      { select: false, label: "时长", bind: "duration" },
    ],
    offers: [
      { payType: "预付费", spec: "包月", duration: "1个月", price: "¥189", originPrice: "", discount: "", limit: "", campaignId: "20250625_discount_buy_dumate_ee_1m" },
      { payType: "预付费", spec: "包年", duration: "1年", price: "¥2268", originPrice: "", discount: "", limit: "", campaignId: "20250625_discount_buy_dumate_ee_12m" },
    ],
  },
  {
    key: "aigc_drama",
    category: "ai",
    title: "短剧漫剧创作平台",
    desc: "一款面向短剧创作者的 AI 辅助创作平台，上传剧本即可生成角色、场景和分镜素材。",
    rows: [
      { select: true, label: "套餐版本", bind: "edition", dim: "row" },
      { select: true, label: "时长", bind: "duration", dim: "col" },
    ],
    offers: [
      [
        { edition: "轻享版", duration: "1个月", price: "¥65.55", originPrice: "¥69", discount: "9.5折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_qxb_1m" },
        { edition: "轻享版", duration: "1年", price: "¥728.64", originPrice: "¥828", discount: "8.8折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_qxb_12m" },
      ],
      [
        { edition: "专业版", duration: "1个月", price: "¥390.45", originPrice: "¥411", discount: "9.5折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_zyb_1m" },
        { edition: "专业版", duration: "1年", price: "¥4340.16", originPrice: "¥4932", discount: "8.8折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_zyb_12m" },
      ],
      [
        { edition: "工作室版S", duration: "1个月", price: "¥946.8", originPrice: "¥1052", discount: "9折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_gzsbs_1m" },
        { edition: "工作室版S", duration: "1年", price: "¥11109.12", originPrice: "¥12624", discount: "8.8折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_gzsbs_12m" },
      ],
      [
        { edition: "工作室版M", duration: "1个月", price: "¥2902.5", originPrice: "¥3225", discount: "9折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_gzsbm_1m" },
        { edition: "工作室版M", duration: "1年", price: "¥34056", originPrice: "¥38700", discount: "8.8折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_gzsbm_12m" },
      ],
      [
        { edition: "工作室版L", duration: "1个月", price: "¥4890.6", originPrice: "¥5434", discount: "9折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_gzsbl_1m" },
        { edition: "工作室版L", duration: "1年", price: "¥57383.04", originPrice: "¥65208", discount: "8.8折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_gzsbl_12m" },
      ],
      [
        { edition: "工作室版XL", duration: "1个月", price: "¥9999", originPrice: "¥11110", discount: "9折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_gzsbxl_1m" },
        { edition: "工作室版XL", duration: "1年", price: "¥117321.6", originPrice: "¥133320", discount: "8.8折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_gzsbxl_12m" },
      ],
      [
        { edition: "工作室版XXL", duration: "1个月", price: "¥17385.3", originPrice: "¥19317", discount: "9折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_gzsbxxl_1m" },
        { edition: "工作室版XXL", duration: "1年", price: "¥203987.52", originPrice: "¥231804", discount: "8.8折", limit: "限购10件", campaignId: "20250625_discount_a_buy_aigc_gzsbxxl_12m" },
      ],
    ],
  },
  {
    key: "miaoda",
    category: "ai",
    title: "秒哒·无代码开发应用",
    desc: "多端同步生成，覆盖网站、小程序、app，企业版可多人协作，企业级安全管控。",
    rows: [
      { select: false, label: "版本", bind: "version" },
      { select: true, label: "时长", bind: "duration", dim: "i" },
    ],
    offers: [
      { version: "企业标准版", duration: "1个月", price: "¥299", originPrice: "¥500", discount: "6折", limit: "限购5件", campaignId: "20250625_discount_buy_miao_da_enterprise_1m" },
      { version: "企业标准版", duration: "12个月", price: "¥3410", originPrice: "¥6000", discount: "5.7折", limit: "限购5件", campaignId: "20250625_discount_buy_miao_da_enterprise_12m" },
    ],
  },
  {
    key: "product_advisor",
    category: "ai",
    title: "产品顾问数字员工",
    desc: "分钟级学习产品资料，7x24 小时精准捕捉访客的高意向瞬间，在专业解答中无感引导用户留资。",
    rows: [
      { select: true, label: "接口", bind: "interfaceName", dim: "row" },
      { select: true, label: "规格", bind: "spec", dim: "col" },
      { select: false, label: "有效期", bind: "duration" },
    ],
    offers: [
      [
        { interfaceName: "专业版", spec: "按月", duration: "1个月", price: "¥539.1", originPrice: "¥599", discount: "9折", limit: "限购10件", campaignId: "20250625_discount_a_buy_ai_pdh_cloud_zyb_1m" },
        { interfaceName: "专业版", spec: "按年", duration: "1年", price: "¥5391", originPrice: "¥5990", discount: "9折", limit: "限购10件", campaignId: "20250625_discount_a_buy_ai_pdh_cloud_zyb_12m" },
      ],
      [
        { interfaceName: "个人版", spec: "按年", duration: "1年", price: "¥468", originPrice: "¥468", discount: "", limit: "限购20件", campaignId: "20250625_discount_a_buy_ai_pdh_cloud_grb_12m" },
      ],
    ],
  },
  {
    key: "qianfan_tokens",
    category: "token",
    title: "千帆大模型 Tokens量包",
    desc: "多模态能力突破，图像理解与跨模态创作显著提升，支持 32K 上下文并优化响应速度。",
    featured: true,
    highlights: ["多模态图像理解与跨模态创作", "支持 32K 上下文", "响应速度优化"],
    rows: [
      { select: false, label: "接口", bind: "interfaceName" },
      { select: true, label: "规格", bind: "spec", dim: "i" },
      { select: false, label: "购买方式", bind: "buyType" },
      { select: false, label: "有效期", bind: "duration" },
    ],
    offers: [
      { interfaceName: "ERNIE-4.5-Turbo-32K", spec: "1亿", buyType: "按Tokens包", duration: "6个月", price: "¥391.5", originPrice: "¥450", discount: "8.7折", limit: "限购5件", campaignId: "20250625_discount_a_buy_wenxinfactory_2352_1y_6" },
      { interfaceName: "ERNIE-4.5-Turbo-32K", spec: "5亿", buyType: "按Tokens包", duration: "6个月", price: "¥1845", originPrice: "¥2250", discount: "8.2折", limit: "限购5件", campaignId: "20250625_discount_a_buy_wenxinfactory_2352_5y_6" },
      { interfaceName: "ERNIE-4.5-Turbo-32K", spec: "10亿", buyType: "按Tokens包", duration: "6个月", price: "¥3465", originPrice: "¥4500", discount: "7.7折", limit: "限购5件", campaignId: "20250625_discount_a_buy_wenxinfactory_2352_10y_6" },
    ],
  },
  {
    key: "video_marketing",
    category: "ai",
    title: "营销内容创作-视频套餐",
    desc: "高效打造视频内容，畅享 60 分钟视频制作，适用于新闻播报、企业营销、教育培训等场景。",
    rows: [
      { select: false, label: "规格", bind: "spec" },
      { select: false, label: "购买方式", bind: "buyType" },
      { select: false, label: "有效期", bind: "duration" },
    ],
    offers: [
      { spec: "月卡", buyType: "按月", duration: "1个月", price: "¥629.1", originPrice: "¥699", discount: "9折", limit: "", campaignId: "20251014_discount_a_buy_ai_dh_cloud_video_1m" },
    ],
  },
  {
    key: "baidu_netdisk",
    category: "ai",
    title: "百度网盘企业版",
    desc: "提供基于强大云存储能力的智能化知识管理、多人共享协作，以及大文件极速传输解决方案。",
    rows: [
      { select: true, label: "套餐版本", bind: "edition", dim: "i" },
      { select: false, label: "时长", bind: "duration" },
    ],
    offers: [
      { edition: "标准版", duration: "1年", price: "¥1499", originPrice: "¥1899", discount: "7.9折", limit: "限购10件", campaignId: "20251013_discount_buy_bnd_e_bzb_12m" },
      { edition: "专业版", duration: "1年", price: "¥3899", originPrice: "¥5499", discount: "7.1折", limit: "限购10件", campaignId: "20251013_discount_buy_bnd_e_zyb_12m" },
      { edition: "旗舰版", duration: "1年", price: "¥6599", originPrice: "¥10999", discount: "6折", limit: "限购10件", campaignId: "20251013_discount_buy_bnd_e_qjb_12m" },
      { edition: "尊享版", duration: "1年", price: "¥9999", originPrice: "¥19999", discount: "5折", limit: "限购10件", campaignId: "20251013_discount_buy_bnd_e_zxb_12m" },
      { edition: "至尊版", duration: "1年", price: "¥19999", originPrice: "¥39999", discount: "5折", limit: "限购10件", campaignId: "20251013_discount_buy_bnd_e_zsb_12m" },
    ],
  },
];
